import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';

import {emptyArray, emptyObject, deprecate} from 'src/utils/index';
import classify from 'src/utils/classify';
import makeClassNameComponent, {
  type ClassNameComponent,
} from 'src/utils/makeClassNameComponent';

import Loading from 'src/components/lib/loading/loading';
import {AutoTruncatedText} from 'src/components/lib/truncated-text/truncated-text';

import ArrowDown from 'src/images/arrow-down.svg';

import css from './table.css';


export type TableHeader = {
  label: React.ReactNode;
  key: string;
  className?: string;
  filterIcon?: React.ReactNode;
  filtered?: boolean;
  subtext?: string;
  sortable?: boolean;
  ContentNode?: React.ReactNode;
  headerIconClassName?: string;
}[];

type Props<Entry extends Record<any, any>> = EmptyRowProps & {
  className?: string;
  Row: React.ComponentType<{
    data: Entry;
    extras: Record<any, any>;
    sortedKeys: string[];
  }>;
  header: TableHeader;
  entries: Array<Entry>;
  extras: Record<any, any>;
  sortable: boolean;
  showHeader?: boolean;
  tableHeaderClassName?: string;
  headerIconClassName?: string;
  defaultSortKey?: string;
  defaultSortDirection: 'asc' | 'desc';
  idName: string;
  onSort?: (key: string, direction: SortDirection) => void;
  EmptyContent?: React.ComponentType<{
    isLoading?: boolean;
  }>;
};

type SortDirection = 'asc' | 'desc';

type EmptyRowProps = {
  emptyText?: React.ReactNode;
  isLoading?: boolean;
};

export class Table<Entry extends Record<any, any>> extends React.PureComponent<
  Props<Entry>,
  {
    sortKey?: string;
    sortDirection: SortDirection;
  }
> {
  static defaultProps: {
    defaultSortDirection: SortDirection;
    entries: any;
    extras: any;
    header: any;
    idName: string;
    sortable: boolean;
    showHeader?: boolean;
  } = {
    header: emptyArray,
    entries: emptyArray,
    extras: emptyObject,
    sortable: true,
    showHeader: true,
    defaultSortDirection: 'asc',
    idName: 'id',
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<Props<Entry>, { sortKey?: string | undefined; sortDirection: SortDirection; }, any>'.
  state: {
    sortDirection: SortDirection;
    sortKey?: string;
  } = {
    sortKey: this.props.defaultSortKey,
    sortDirection: this.props.defaultSortDirection,
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<Props<Entry>, { sortKey?: string | undefined; sortDirection: SortDirection; }, any>'.
  render(): React.ReactNode {
    const {
      className,
      Row,
      entries,
      extras,
      header,
      idName,
      sortable,
      isLoading,
      emptyText,
      EmptyContent,
      showHeader,
      tableHeaderClassName,
      headerIconClassName,
      onSort,
    } = this.props;
    const {sortKey, sortDirection} = this.state;
    const sortEntries = () => {
      if (onSort) {
        return entries;
      } else {
        const sortedEntries = sortKey
          ? sortBy(entries, (entry) => {
              const value = sortKey
                .split('.')
                .reduce(
                  (currentEntryLevel, property) => currentEntryLevel[property],
                  entry,
                );
              if (typeof value === 'string') {
                // @ts-expect-error - TS2339 - Property 'toLowerCase' does not exist on type 'never'.
                return value.toLowerCase();
              } else if (typeof value === 'object') {
                return value?.length || 0;
              } else {
                return value;
              }
            })
          : entries;
        if (sortDirection === 'desc') {
          sortedEntries.reverse();
        }
        return sortedEntries;
      }
    };

    const sortedEntries = sortEntries();

    const sortedKeys = sortedEntries.map((ent) => get(ent, idName));

    return (
      <TableBox className={className}>
        {showHeader && (
          <header
            className={classify(css.tableHeaderSortable, tableHeaderClassName)}
          >
            {header.map(
              ({
                key,
                label,
                subtext,
                filterIcon,
                filtered,
                ContentNode,
                className,
                sortable: cellSortable = true,
              }) => {
                let headerClassName;
                const filterable = Boolean(filterIcon);
                if ((sortable && cellSortable) || filterable) {
                  headerClassName = classify(
                    css.headerCellSortable,
                    {
                      [css.sorted]: sortKey === key,
                      [css.filtered]: filtered,
                    },
                    css[sortDirection],
                    className,
                  );
                } else {
                  headerClassName = classify(css.headerCell, className);
                }
                return (
                  <div
                    className={headerClassName}
                    key={key}
                    // @ts-expect-error - TS2322 - Type '(() => void) | null' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
                    onClick={
                      sortable && cellSortable
                        ? () => {
                            this.handleSortClick(key);
                          }
                        : null
                    }
                  >
                    {ContentNode}
                    {!ContentNode && (
                      <>
                        <div className={css.labelContainer}>
                          {label}
                          <span className={css.headerSubtext}>
                            {subtext && subtext}
                          </span>
                        </div>
                        {(sortable || filterIcon != null) && (
                          <div
                            className={classify(
                              css.headerIconContainer,
                              headerIconClassName,
                            )}
                          >
                            {sortable && (
                              <ArrowDown className={css.sortArrow} />
                            )}
                            {filterIcon != null && (
                              <div className={css.filterIcon}>{filterIcon}</div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              },
            )}
          </header>
        )}

        <div className={css.tableBodyV2}>
          {isLoading || !sortedEntries.length ? (
            EmptyContent != null ? (
              <EmptyContent isLoading={isLoading} />
            ) : (
              <EmptyRow isLoading={isLoading} emptyText={emptyText} />
            )
          ) : (
            sortedEntries.map((entry) => (
              <Row
                data={entry}
                extras={extras}
                sortedKeys={sortedKeys}
                key={get(entry, idName)}
              />
            ))
          )}
        </div>
      </TableBox>
    );
  }

  handleSortClick(sortKey: string) {
    if (sortKey === this.state.sortKey) {
      const sortDirection = this.state.sortDirection === 'asc' ? 'desc' : 'asc';
      this.setState({sortDirection}, () => {
        if (this.props.onSort) {
          this.props.onSort(sortKey, sortDirection);
        }
      });
    } else {
      this.setState({sortKey});
      if (this.props.onSort && this.state?.sortDirection) {
        this.props.onSort(sortKey, this.state.sortDirection);
      }
    }
  }
}

export default Table;

type CellsProps = {
  children?: React.ReactNode;
  className?: string | null | undefined;
  truncatedText?: React.ReactNode;
  onClick?:
    | ((arg1: React.SyntheticEvent<HTMLElement>) => unknown)
    | null
    | undefined;
};

// @ts-expect-error - TS2322 - Type 'AbstractComponent<ComponentProps<C>, unknown>' is not assignable to type 'ClassNameComponent'.
export const TableBox: ClassNameComponent = makeClassNameComponent(css.table);

export const Cells = ({
  children,
  className,
  ...rest
}: CellsProps): React.ReactElement<React.ComponentProps<'div'>> => (
  // @ts-expect-error - TS2322 - Type '{ children: ReactNode; truncatedText?: ReactNode; onClick?: ((arg1: SyntheticEvent<HTMLElement, Event>) => unknown) | null | undefined; className: string; }' is not assignable to type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'.
  <div className={classify(css.row, className)} {...rest}>
    {children}
  </div>
);
export const Row = Cells;

export const Cell = ({
  children,
  truncatedText,
  className,
}: CellsProps): React.ReactElement<React.ComponentProps<'div'>> => {
  if (React.isValidElement(children) && truncatedText == null) {
    deprecate({
      reason: `Cell cannot have a React.Node as its 'text', please use the 'truncatedText' prop or a BasicCell.`,
      person: 'Marcos',
    });
  }

  return (
    <div className={classify(css.cell, className)}>
      <AutoTruncatedText text={children} tipContent={truncatedText} />
    </div>
  );
};

export const BasicCell = ({
  children,
  className,
}: CellsProps): React.ReactElement<React.ComponentProps<'div'>> => (
  <div className={classify(css.cell, className)}>{children}</div>
);

export const TruncatedCell = ({
  content,
  className,
  qaId,
}: {
  content: string | React.ReactNode;
  className: string | null | undefined;
  qaId?: string;
}): React.ReactElement<React.ComponentProps<'div'>> => (
  <div className={classify(css.cell, className)} data-qa-id={qaId}>
    <AutoTruncatedText text={content} />
  </div>
);

// @ts-expect-error TS2322
export const TableContainer: Flow_AbstractComponent<
  React.ComponentProps<never>
> = makeClassNameComponent(css.tableContainer);
// @ts-expect-error TS2322
export const TablePage: Flow_AbstractComponent<React.ComponentProps<never>> =
  makeClassNameComponent(css.content);

export const TablePageHeader: Flow_AbstractComponent<
  // @ts-expect-error TS2344
  React.ComponentProps<string>
> = makeClassNameComponent(css.header, 'header');
// @ts-expect-error - TS2322 - Type 'AbstractComponent<ComponentProps<C>, unknown>' is not assignable to type 'ClassNameComponent'.
export const TableActions: ClassNameComponent = makeClassNameComponent(
  css.actionsNew,
);

// @ts-expect-error TS2322
export const Page: Flow_AbstractComponent<React.ComponentProps<never>> =
  makeClassNameComponent(css.content2);
// @ts-expect-error TS2322
export const HeaderSection: Flow_AbstractComponent<
  React.ComponentProps<never>
> = makeClassNameComponent(css.headerSection);

export const EmptyRow = ({
  isLoading,
  emptyText,
}: EmptyRowProps): React.ReactElement<React.ComponentProps<'div'>> => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <div className={css.emptyRow}>
      {isLoading ? (
        <Loading className={css.loader} />
      ) : (
        emptyText ||
        `${t('Nothing_to_display_here', 'Nothing to display here')}.`
      )}
    </div>
  );
};
